import { useTranslation } from 'react-i18next'
import { ProfileSwitcher } from '@/components/terminology/ProfileSwitcher'
import { LocaleManager } from '@/components/terminology/LocaleManager'
import { NAMESPACES } from '@/i18n/termKeys'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion'

// Each preview row: a display label + the namespaced i18next key (+ optional opts).
type PreviewRow = { label: string; key: string; opts?: Record<string, unknown> }
const PREVIEW_GROUPS: { ns: string; rows: PreviewRow[] }[] = [
  {
    ns: 'cgt',
    rows: [
      { label: 'app_title', key: 'cgt:app_title' },
      { label: 'program', key: 'cgt:program' },
      { label: 'site', key: 'cgt:site' },
    ],
  },
  {
    ns: 'subject',
    rows: [
      { label: 'subject', key: 'subject:subject' },
      { label: 'subject_plural', key: 'subject:subject_plural' },
      { label: 'caregiver', key: 'subject:caregiver' },
      { label: 'referring_phys', key: 'subject:referring_phys' },
    ],
  },
  {
    ns: 'clinical_status',
    rows: [
      { label: 'deceased', key: 'clinical_status:deceased' },
      { label: 'adverse_event', key: 'clinical_status:adverse_event' },
      { label: 'remission', key: 'clinical_status:remission' },
      { label: 'relapse', key: 'clinical_status:relapse' },
      { label: 'eligibility', key: 'clinical_status:eligibility' },
    ],
  },
  {
    ns: 'manufacturing',
    rows: [
      { label: 'apheresis', key: 'manufacturing:apheresis' },
      { label: 'collection', key: 'manufacturing:collection' },
      { label: 'manufacturing', key: 'manufacturing:manufacturing' },
      { label: 'cryopreservation', key: 'manufacturing:cryopreservation' },
      { label: 'disposition', key: 'manufacturing:disposition' },
      { label: 'coi', key: 'manufacturing:coi' },
      { label: 'coc', key: 'manufacturing:coc' },
      { label: 'product', key: 'manufacturing:product' },
      { label: 'infusion', key: 'manufacturing:infusion' },
      { label: 'dose (1)', key: 'manufacturing:dose_count', opts: { count: 1 } },
      { label: 'dose (3)', key: 'manufacturing:dose_count', opts: { count: 3 } },
    ],
  },
  {
    ns: 'scheduling',
    rows: [
      { label: 'slot', key: 'scheduling:slot' },
      { label: 'manufacturing_slot', key: 'scheduling:manufacturing_slot' },
      { label: 'visit', key: 'scheduling:visit' },
    ],
  },
  {
    ns: 'consent',
    rows: [
      { label: 'informed_consent', key: 'consent:informed_consent' },
      { label: 'protocol', key: 'consent:protocol' },
      { label: 'enrollment', key: 'consent:enrollment' },
    ],
  },
]

/** Live demo so edits/profile switches are visible immediately. */
function TerminologyPreview() {
  // Load every namespace; use namespaced keys ("ns:key") with plain react-i18next.
  const { t } = useTranslation([...NAMESPACES])

  return (
    <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
      {PREVIEW_GROUPS.map((group) => (
        <div key={group.ns} className="flex flex-col gap-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {group.ns}
          </p>
          <dl className="flex flex-col gap-2 text-sm">
            {group.rows.map((row) => (
              <div key={row.label} className="flex flex-col">
                <dt className="font-mono text-xs text-muted-foreground">{row.label}</dt>
                <dd className="text-foreground">{t(row.key, row.opts)}</dd>
              </div>
            ))}
          </dl>
        </div>
      ))}
    </div>
  )
}

/** Language & terminology configuration: active toggle + languages list + preview. */
export function LanguageTermsSettings() {
  return (
    <div className="flex flex-col gap-6">
      {/* Active terminology bar */}
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border bg-muted/30 px-4 py-3">
        <div>
          <h2 className="text-sm font-semibold text-foreground">Active terminology</h2>
          <p className="text-sm text-muted-foreground">
            The profile currently used across the app.
          </p>
        </div>
        <ProfileSwitcher />
      </div>

      {/* Languages management (add / edit terms / CSV / delete — all via dialogs) */}
      <LocaleManager />

      {/* Collapsible live preview */}
      <Accordion type="single" collapsible className="rounded-lg border px-4">
        <AccordionItem value="preview" className="border-none">
          <AccordionTrigger className="text-sm font-medium">
            Preview active terminology
          </AccordionTrigger>
          <AccordionContent className="pb-4">
            <TerminologyPreview />
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  )
}
